import React, { useState, useRef, useEffect } from "react";

const mockUsers = [
  { id: 1, name: "John Doe", lastMsg: "I need help with my order.", lastMsgTime: new Date() },
  { id: 2, name: "Sarah Smith", lastMsg: "When will my package arrive?", lastMsgTime: new Date() },
  { id: 3, name: "Michael Lee", lastMsg: "How to reset my password?", lastMsgTime: new Date() },
];

const aiReplies = [
  "Okay, let me check that for you right away.",
  "My order details is as follows...",
  "I understand, let me check on that.",
  "Okay, I’m looking into it now.",
  "Thank you for your patience.",
];

const HelpChat = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState({});
  const [newMessage, setNewMessage] = useState("");
  const [userList, setUserList] = useState(mockUsers);
  const [showSidebar, setShowSidebar] = useState(false);

  const messagesEndRef = useRef(null);
  const scrollPositions = useRef({});

  const handleUserClick = (user) => {
    if (selectedUser && messagesEndRef.current) {
      scrollPositions.current[selectedUser.id] = messagesEndRef.current.scrollTop;
    }
    setSelectedUser(user);
    setShowSidebar(false);

    if (!messages[user.id]) {
      setMessages((prev) => ({
        ...prev,
        [user.id]: [
          {
            id: Date.now(),
            sender: "customer",
            text: user.lastMsg,
            time: user.lastMsgTime,
          },
        ],
      }));
    }
  };

  const handleSend = () => {
    if (!newMessage.trim() || !selectedUser) return;

    const now = new Date();
    const msg = { id: Date.now(), sender: "admin", text: newMessage, time: now };
    const updatedMessages = {
      ...messages,
      [selectedUser.id]: [...(messages[selectedUser.id] || []), msg],
    };

    setMessages(updatedMessages);
    setNewMessage("");

    setUserList((prev) =>
      prev
        .map((u) =>
          u.id === selectedUser.id
            ? { ...u, lastMsg: newMessage, lastMsgTime: now }
            : u
        )
        .sort((a, b) => b.lastMsgTime - a.lastMsgTime)
    );

    setTimeout(() => {
      const aiMsg = {
        id: Date.now() + 1,
        sender: "customer",
        text: aiReplies[Math.floor(Math.random() * aiReplies.length)],
        time: new Date(),
      };
      setMessages((prev) => ({
        ...prev,
        [selectedUser.id]: [...(prev[selectedUser.id] || []), aiMsg],
      }));
    }, 1000);
  };

  useEffect(() => {
    if (selectedUser && messagesEndRef.current) {
      const scrollTop = scrollPositions.current[selectedUser.id];
      if (scrollTop !== undefined) {
        messagesEndRef.current.scrollTop = scrollTop;
      } else {
        messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
      }
    }
  }, [selectedUser, messages[selectedUser?.id]]);

  return (
    <div className="flex h-[80vh] mx-2 sm:mx-6 mt-6 bg-white dark:bg-[#1A1A1B] rounded-xl shadow-md overflow-hidden">
      {/* Sidebar */}
      <div
        className={`fixed md:static top-26 left-0  h-full w-64 bg-white dark:bg-[#1A1A1B] border-r dark:border-[#3D3D3D] p-4 z-20 transform transition-transform duration-300 
        ${showSidebar ? "translate-x-0" : "-translate-x-full"} 
        md:translate-x-0`}
      >
        <h2 className="text-2xl agarly-gradient-text font-semibold mb-4">
          Conversations
        </h2>
        <ul className="space-y-2">
          {userList.map((user) => (
            <li
              key={user.id}
              onClick={() => handleUserClick(user)}
              className={`p-3 rounded-lg cursor-pointer transition ${
                selectedUser?.id === user.id
                  ? "bg-[#5DEBFF] text-white dark:text-gray-900"
                  : "hover:bg-gray-100 dark:hover:bg-[#2D2D2D] dark:text-gray-300"
              }`}
            >
              <div className="flex justify-between items-center">
                <div className="font-medium">{user.name}</div>
                <div className="text-xs opacity-70">
                  {new Date(user.lastMsgTime).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
              <div className="text-xs opacity-70 truncate">{user.lastMsg}</div>
            </li>
          ))}
        </ul>
      </div>

      {/* Chat Window */}
      <div className="flex-1 flex flex-col relative">
        {/* Mobile toggle button */}
        <button
          className="absolute top-3 left-3 md:hidden bg-[#5DEBFF] text-white px-3 py-1 rounded-lg shadow"
          onClick={() => setShowSidebar(!showSidebar)}
        >
          ☰
        </button>

        {selectedUser ? (
          <>
            {/* Header */}
            <div className="p-4 border-b dark:border-[#3D3D3D] text-center md:text-left">
              <h3 className="text-lg dark:text-gray-200">
                {selectedUser.name}
              </h3>
            </div>

            {/* Messages */}
            <div
              ref={messagesEndRef}
              className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-black"
            >
              {messages[selectedUser.id]?.map((msg, i, arr) => {
                const showDate =
                  i === 0 ||
                  new Date(arr[i].time).toDateString() !==
                    new Date(arr[i - 1].time).toDateString();
                return (
                  <div key={msg.id}>
                    {showDate && (
                      <div className="text-center text-xs text-gray-500 my-2">
                        {new Date(msg.time).toDateString()}
                      </div>
                    )}
                    <div
                      className={`flex ${
                        msg.sender === "admin" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`px-4 py-2 rounded-xl max-w-xs text-sm ${
                          msg.sender === "admin"
                            ? "bg-[#5DEBFF] text-white rounded-br-none"
                            : "bg-gray-200 dark:bg-[#1A1A1B] dark:text-gray-300 rounded-bl-none"
                        }`}
                      >
                        <div>{msg.text}</div>
                        <div className="text-[10px] opacity-60 text-right mt-1">
                          {new Date(msg.time).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Input */}
            <div className="p-3 border-t dark:border-[#3D3D3D] flex items-center gap-2">
              <input
                type="text"
                className="flex-1 p-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-500 dark:border-gray-600 dark:bg-[#1A1A1B] dark:text-gray-200"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
              />
              <button
                onClick={handleSend}
                className="agarly-btn-primary text-agarly-dark px-4 py-2 rounded-lg dark:text-gray-900"
              >
                Send
              </button>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center flex-1 dark:text-gray-200">
            Select a conversation to start chatting
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpChat;
